import * as React from 'react';
import { cn } from '@/shared/lib';
import { LockKeyholeIcon } from 'lucide-react';
import { selectIsMinimalistic, useAppSelector } from '@/app/store';
import * as TabsPrimitive from '@radix-ui/react-tabs';

function Tabs({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return <TabsPrimitive.Root data-slot='tabs' className={cn('flex flex-col gap-2', className)} {...props} />;
}

function TabsList({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.List>) {
  const isMinimalistic = useAppSelector(selectIsMinimalistic);

  return (
    <TabsPrimitive.List
      data-slot='tabs-list'
      className={cn(
        'w-full inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground',
        isMinimalistic && 'rounded-none bg-primary/5',
        className,
      )}
      {...props}
    />
  );
}

function TabsTrigger({ disabled, children, className, ...props }: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  const isMinimalistic = useAppSelector(selectIsMinimalistic);

  return (
    <TabsPrimitive.Trigger
      disabled={disabled}
      data-slot='tabs-trigger'
      className={cn(
        'w-full cursor-pointer data-[state=active]:text-primary inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:text-sidebar-ring disabled:pointer-events-none disabled:opacity-60 data-[state=active]:bg-background data-[state=active]:shadow',
        isMinimalistic && 'rounded-none',
        className,
      )}
      {...props}
    >
      {disabled && <LockKeyholeIcon size={12} className='mr-1' />}

      {children}
    </TabsPrimitive.Trigger>
  );
}

function TabsContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot='tabs-content'
      className={cn(
        'mt-3 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        className,
      )}
      {...props}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
